import React, { useEffect, useState, lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Nav from './components/Nav';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from './context/auth/AuthContext';
import { LibraryProvider } from './context/library/LibraryContext';
import "./App.css";
import Footer from "./components/Footer";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
    },
  },
});

const Landing = lazy(() => import('./components/Landing'));
const Tv = lazy(() => import('./components/Tv'));
const Detail = lazy(() => import('./components/Detail').then((m) => ({ default: m.Detail })));
const TvDetail = lazy(() => import('./components/TvDetail'));
const SeasonDetail = lazy(() => import('./components/SeasonDetail'));
const Search = lazy(() => import('./components/Search'));
const AuthPage = lazy(() => import('./components/AuthPage'));
const AuthCallback = lazy(() => import('./components/AuthCallback'));
const WatchlistPage = lazy(() => import('./components/WatchlistPage'));

const PageFallback = () => (
  <div className="flex min-h-[40vh] items-center justify-center">
    <div className="spinner" />
  </div>
);

function LegacyMovieRedirect() {
  const { id, title } = useParams();
  return <Navigate to={`/movies/${id}/${title}`} replace />;
}

function App() {
  const [connection, setConnection] = useState(true);

  useEffect(() => {
    const goOffline = () => setConnection(false);
    const goOnline = () => setConnection(true);
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
    };
  }, []);

  if (!connection) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-black px-4 py-10">
        <div className="double-shell w-full max-w-xl">
          <div className="double-core px-8 py-14 text-center sm:px-12">
            <h1 className="headline text-5xl sm:text-6xl">You&apos;re offline</h1>
            <p className="mx-auto mt-4 max-w-md text-sm leading-7 text-[var(--text-secondary)]">
              Reconnect to keep browsing films, series, cast, trailers, and discovery lists.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <LibraryProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <div className="relative min-h-[100dvh] overflow-hidden bg-black text-[var(--text-strong)]">
              <div className="relative z-10 flex min-h-[100dvh] flex-col">
                <Nav />

                <main className="flex-1 px-6 pb-8 pt-24 sm:px-10 lg:px-16 xl:px-24">
                    <Suspense fallback={<PageFallback />}>
                      <Routes>
                        <Route path="/" element={<Navigate to="/movies" replace />} />
                        <Route path="/movies" element={<Landing />} />
                        <Route path="/tv" element={<Tv />} />
                        <Route path="/movies/:id/:title" element={<Detail />} />
                        <Route path="/movie/:id/:title" element={<LegacyMovieRedirect />} />
                        <Route path="/tv/:id/:title" element={<TvDetail />} />
                        <Route path="/tv/:id/:title/season/:seasonNumber/:seasonTitle" element={<SeasonDetail />} />
                        <Route path="/search" element={<Search />} />
                        <Route path="/login" element={<AuthPage />} />
                        <Route path="/signup" element={<Navigate to="/login" replace />} />
                        <Route path="/auth/callback" element={<AuthCallback />} />
                        <Route
                          path="/watchlist"
                          element={
                            <ProtectedRoute>
                              <WatchlistPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route path="/watched" element={<Navigate to="/watchlist" replace />} />
                        <Route path="*" element={<Navigate to="/movies" replace />} />
                      </Routes>
                    </Suspense>
                </main>
                <Footer />
              </div>
            </div>
          </Router>
        </LibraryProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
